import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore, triggerAuthRedirect } from '@/stores/authStore';
import { getMainApiBaseUrl } from '@/config/apiEnv';

export const api: AxiosInstance = axios.create({
  baseURL: getMainApiBaseUrl(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

function extractApiErrorMessage(error: AxiosError): string {
  const data = error.response?.data as { message?: string; error?: string } | undefined;
  return data?.message || data?.error || error.message || 'An unexpected error occurred';
}

// Request interceptor - add auth token (skip public auth endpoints)
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const url = config.url || '';
    const isPublicAuth =
      url.includes('/auth/login') ||
      url.includes('/auth/register') ||
      url.includes('/auth/password-reset');
    if (isPublicAuth) {
      return config;
    }
    const tokens = useAuthStore.getState().tokens;
    if (tokens?.token) {
      config.headers.Authorization = `Bearer ${tokens.token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let failedRefreshQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

function processRefreshQueue(error: unknown, newToken: string | null) {
  failedRefreshQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(newToken);
    }
  });
  failedRefreshQueue = [];
}

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Guard: never retry the refresh endpoint itself to prevent infinite loops
    const isRefreshRequest =
      originalRequest?.url?.includes('/auth/refresh') || originalRequest?.url?.endsWith('/refresh');

    const isPublicAuthRequest =
      originalRequest?.url?.includes('/auth/login') ||
      originalRequest?.url?.includes('/auth/register') ||
      originalRequest?.url?.includes('/auth/password-reset');

    // Handle 401 Unauthorized - try to refresh token
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isRefreshRequest &&
      !isPublicAuthRequest
    ) {
      const hadToken = Boolean(useAuthStore.getState().tokens?.token);
      if (!hadToken) {
        const errorMessage = extractApiErrorMessage(error);
        return Promise.reject(new Error(errorMessage));
      }

      if (isRefreshing) {
        // Queue this request until the ongoing refresh completes
        return new Promise<unknown>((resolve, reject) => {
          failedRefreshQueue.push({ resolve, reject });
        }).then((newToken) => {
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await useAuthStore.getState().refreshToken();
        const tokens = useAuthStore.getState().tokens;
        const newToken = tokens?.token || null;
        processRefreshQueue(null, newToken);
        isRefreshing = false;

        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        processRefreshQueue(refreshError, null);
        isRefreshing = false;
        useAuthStore.getState().logout();
        triggerAuthRedirect();
        return Promise.reject(refreshError);
      }

      // If refreshToken returned without error but also without a token (not authenticated)
      useAuthStore.getState().logout();
      triggerAuthRedirect();
      return Promise.reject(new Error('Authentication required'));
    }

    // Handle other errors
    const errorMessage = extractApiErrorMessage(error);
    
    return Promise.reject(new Error(errorMessage));
  }
);

// Error types for better error handling
export interface ApiErrorResponse {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export function handleApiError(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as ApiErrorResponse | undefined;
    return data?.message || (error.response?.data as { error?: string } | undefined)?.error || error.message;
  }
  return 'An unexpected error occurred';
}